import { useState, useRef, useEffect } from 'react';
import apiClient from '../api/client';
import { track } from '../utils/analytics';

/**
 * useWhisper — records audio via MediaRecorder, sends to backend /transcribe,
 * which calls OpenAI Whisper and returns the transcript.
 *
 * States:
 *   recording — mic is open and capturing audio
 *   transcribing — audio sent, waiting for Whisper response
 *   error — 'not-allowed' | 'not-supported' | 'network' | 'unknown'
 *
 * Auto-stop: once the user has actually started speaking, the recorder stops on
 * its own after ~3 s of silence (so you don't have to tap stop). Tapping the
 * mic again still stops it immediately. Pass { autoStopMs: 0 } to disable.
 */
export function useWhisper(language = 'it') {
  const [recording, setRecording]       = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript]     = useState('');
  const [error, setError]               = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const streamRef        = useRef(null);
  const onResultRef      = useRef(null);

  // Silence-detection plumbing
  const audioCtxRef      = useRef(null);
  const silenceTimerRef  = useRef(null);

  const supported = !!(
    typeof window !== 'undefined' &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia &&
    window.MediaRecorder
  );

  // Release the microphone if the component using this hook unmounts while
  // still recording (e.g. user swipes to another page mid-dictation). We drop
  // the recorder's handlers first so it doesn't fire a transcription for a form
  // that no longer exists, then stop the stream tracks to free the mic.
  useEffect(() => {
    return () => {
      if (silenceTimerRef.current) { clearInterval(silenceTimerRef.current); silenceTimerRef.current = null; }
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_) {} audioCtxRef.current = null; }
      const mr = mediaRecorderRef.current;
      if (mr) {
        mr.ondataavailable = null;
        mr.onstop = null;
        try { if (mr.state !== 'inactive') mr.stop(); } catch (_) {}
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  function teardownSilence() {
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
  }

  // Watch the mic level; once speech has been detected, stop after `autoStopMs`
  // of continuous quiet. Never fires before the user has spoken, so a slow
  // start won't cut them off.
  function setupSilenceDetection(stream, autoStopMs) {
    if (!autoStopMs) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return; // graceful: no auto-stop, manual tap still works
    try {
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);

      const THRESHOLD = 0.018;   // RMS above this counts as "speech"
      let speechStarted = false;
      let lastVoiceTs = Date.now();

      silenceTimerRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const now = Date.now();
        if (rms > THRESHOLD) { speechStarted = true; lastVoiceTs = now; }
        if (speechStarted && now - lastVoiceTs > autoStopMs) {
          stopRecording();
        }
      }, 150);
    } catch (_) {
      // Any Web Audio failure: fall back to manual stop only.
      teardownSilence();
    }
  }

  async function startRecording(onResult, opts = {}) {
    const autoStopMs = opts.autoStopMs === undefined ? 3000 : opts.autoStopMs;
    setError(null);
    setTranscript('');
    chunksRef.current = [];
    onResultRef.current = onResult;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick the best supported MIME type.
      // 16 kbps is plenty for Whisper (speech-only) and produces ~2 KB/s —
      // ~8× smaller than the default, so upload time drops from ~2 s to ~0.3 s.
      const mimeType = getSupportedMime();
      const recorderOpts = { audioBitsPerSecond: 16_000 };
      if (mimeType) recorderOpts.mimeType = mimeType;
      const mediaRecorder = new MediaRecorder(stream, recorderOpts);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        // Stop the silence watcher and release the mic immediately
        teardownSilence();
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
          const formData = new FormData();
          formData.append('audio', blob, `recording.${mimeType?.includes('ogg') ? 'ogg' : 'webm'}`);

          // Do NOT set Content-Type manually — axios must set it automatically
          // so it includes the correct multipart boundary
          const res = await apiClient.post(`/transcribe?language=${language}`, formData);

          const text = (res.data.transcript || '').trim();
          setTranscript(text);
          if (text && onResultRef.current) onResultRef.current(text);
          // Analytics: voice dictation used successfully (no transcript sent).
          if (text) track('voice_used', { language });
        } catch (err) {
          if (err.response?.status === 429 && err.response?.data?.code === 'AI_LIMIT_REACHED') {
            // Free-plan monthly AI allowance used up — consumers show a friendly message
            setError('ai-limit');
          } else {
            // Surface the real server error message so we can diagnose it
            const serverMsg = err.response?.data?.error || err.message || 'unknown';
            console.error('Whisper transcription error:', err.response?.status, serverMsg);
            setError('network:' + serverMsg);
          }
        } finally {
          setTranscribing(false);
        }
      };

      mediaRecorder.start();
      setRecording(true);
      setupSilenceDetection(stream, autoStopMs);
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('not-allowed');
      } else {
        setError('unknown');
      }
    }
  }

  function stopRecording() {
    teardownSilence();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  }

  function clearTranscript() {
    setTranscript('');
  }

  return {
    recording,
    transcribing,
    transcript,
    error,
    supported,
    startRecording,
    stopRecording,
    clearTranscript,
  };
}

function getSupportedMime() {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return null;
}
